<script lang="ts">
    import { onMount } from 'svelte'
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { Checkbox } from "@/lib/components/ui/checkbox";
    import * as Dialog from "@/lib/components/ui/dialog";
    import SocialButtons from "./SocialButtons.svelte";
    import { getRequest } from "@/Util";

    export let recaptchaKey: string
    export let onLogin: () => void
    export let onSuccess: (email: string) => void

    let firstName = ''
    let lastName = ''
    let email = ''
    let username = ''
    let password = ''
    let showPassword = false
    let isAdult = false
    let agreedToTerms = false
    let loading = false
    let tosOpen = false
    let ppOpen = false
    let fieldErrors: Record<string, string> = {}
    let serverError = ''

    // reCAPTCHA loads whenever a site key is configured. It is spam/abuse
    // prevention for the signup endpoint — strictly necessary to operate the form,
    // so it is covered by the cookie notice rather than gated behind consent.
    //
    // This is reCAPTCHA v2 *invisible*: there is no checkbox to tick, so instead of
    // reading a token the user produced before pressing the button, submit() asks
    // for one on demand via grecaptcha.execute() and waits for the callback. Most
    // visitors never see anything; suspicious ones get the image challenge.
    let recaptchaWidgetId: number | null = null
    let recaptchaContainer: HTMLDivElement

    // Pending grecaptcha.execute() handlers — settled by the widget callbacks below.
    let resolveCaptcha: ((token: string) => void) | null = null
    let rejectCaptcha: ((reason: string) => void) | null = null
    let captchaTimer: ReturnType<typeof setTimeout> | null = null

    const CAPTCHA_TIMEOUT_MS = 120000

    $: captchaEnabled = !!recaptchaKey

    $: if (captchaEnabled && recaptchaContainer) {
        loadRecaptcha()
    }

    function loadRecaptcha() {
        const scriptId = 'recaptcha-script'
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script')
            script.id = scriptId
            script.src = 'https://www.google.com/recaptcha/api.js?onload=xertRecaptchaReady&render=explicit'
            script.async = true
            script.defer = true
            document.head.appendChild(script)
        }
        ;(window as any).xertRecaptchaReady = () => {
            if (recaptchaContainer && recaptchaWidgetId === null) {
                recaptchaWidgetId = (window as any).grecaptcha.render(recaptchaContainer, {
                    sitekey: recaptchaKey,
                    size: 'invisible',
                    badge: 'bottomright',
                    callback: (token: string) => resolveCaptcha?.(token),
                    'expired-callback': () => rejectCaptcha?.('Verification expired. Please try again.'),
                    'error-callback': () => rejectCaptcha?.('Verification failed. Please check your connection and try again.'),
                })
            }
        }
        if ((window as any).grecaptcha?.render) {
            ;(window as any).xertRecaptchaReady()
        }
    }

    onMount(() => {
        if (captchaEnabled) loadRecaptcha()

        return () => {
            if (captchaTimer !== null) clearTimeout(captchaTimer)
        }
    })

    /**
     * Run the invisible challenge and resolve with the resulting token.
     *
     * Rejects with a user-facing message rather than an Error — every caller does
     * is put it in fieldErrors.captcha.
     */
    function requestCaptchaToken(): Promise<string> {
        const grecaptcha = (window as any).grecaptcha

        if (recaptchaWidgetId === null || !grecaptcha?.execute) {
            return Promise.reject('Verification is still loading. Please wait a moment and try again.')
        }

        return new Promise<string>((resolve, reject) => {
            // Whichever callback fires first wins and tears down the rest, so a
            // late expiry can't reject a submission that already went through.
            const settle = (fn: (value: any) => void) => (value: any) => {
                if (captchaTimer !== null) {
                    clearTimeout(captchaTimer)
                    captchaTimer = null
                }
                resolveCaptcha = null
                rejectCaptcha = null
                fn(value)
            }

            resolveCaptcha = settle(resolve)
            rejectCaptcha = settle(reject)

            // Closing the challenge popup fires no callback at all, so without a
            // timeout this promise — and the form's loading state — would hang
            // forever on a dismissed challenge.
            captchaTimer = setTimeout(
                () => rejectCaptcha?.('Verification timed out. Please try again.'),
                CAPTCHA_TIMEOUT_MS,
            )

            // Clear any token left by a previous attempt; execute() is a no-op
            // while a token is still held.
            grecaptcha.reset(recaptchaWidgetId)
            grecaptcha.execute(recaptchaWidgetId)
        })
    }

    function passwordStrength(pw: string): { level: number; label: string; color: string } {
        if (!pw) return { level: 0, label: '', color: '' }
        let score = 0
        if (pw.length >= 8) score++
        if (pw.length >= 12) score++
        if (/[A-Z]/.test(pw)) score++
        if (/[0-9]/.test(pw)) score++
        if (/[^A-Za-z0-9]/.test(pw)) score++
        if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-red-500' }
        if (score <= 3) return { level: 2, label: 'Fair', color: 'bg-yellow-500' }
        return { level: 3, label: 'Strong', color: 'bg-green-500' }
    }

    $: strength = passwordStrength(password)

    async function submit() {
        fieldErrors = {}
        serverError = ''

        if (!isAdult) {
            fieldErrors.adult = 'You must confirm you are 16 or older.'
            return
        }
        if (!agreedToTerms) {
            fieldErrors.terms = 'You must agree to the Terms of Service and Privacy Policy.'
            return
        }

        loading = true

        let captchaResponse = ''
        if (captchaEnabled) {
            // Invisible reCAPTCHA produces its token here, at submit time — there
            // is nothing for the user to have completed beforehand.
            try {
                captchaResponse = await requestCaptchaToken()
            } catch (message) {
                fieldErrors.captcha = typeof message === 'string'
                    ? message
                    : 'Verification failed. Please try again.'
                loading = false
                return
            }
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        try {
            const body: Record<string, unknown> = {
                first_name: firstName,
                last_name: lastName,
                email,
                username,
                password,
                password_confirmation: password,
                tnc: agreedToTerms ? '1' : '',
                i_am_sixteen: isAdult ? '1' : '',
                timezone,
            }
            // Only include captcha token when a key is configured
            if (captchaResponse) body['g-recaptcha-response'] = captchaResponse

            const res = await fetch('/auth/register', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify(body),
            })
            const data = await res.json().catch(() => ({}))
            if (res.ok && data.success) {
                onSuccess(email)
            } else if (res.status === 429) {
                serverError = 'Too many sign-up attempts. Please wait a minute and try again.'
            } else if (data?.errors) {
                // The server names the consent fields tnc / i_am_sixteen; the form
                // shows those errors under the terms / adult checkboxes.
                const alias: Record<string, string> = { tnc: 'terms', i_am_sixteen: 'adult' }
                const next: Record<string, string> = {}
                for (const [k, msgs] of Object.entries(data.errors as Record<string, string[]>)) {
                    next[alias[k] ?? k] = (msgs as string[])[0]
                }
                fieldErrors = next
            } else {
                serverError = data?.message ?? 'Registration failed. Please try again.'
            }
        } catch {
            serverError = 'A network error occurred. Please try again.'
        } finally {
            loading = false
            if (captchaEnabled && recaptchaWidgetId !== null) {
                (window as any).grecaptcha?.reset(recaptchaWidgetId)
            }
        }
    }
</script>

<div class="space-y-5">
    <div class="space-y-2 text-center">
        <h1 class="text-2xl font-semibold tracking-tight">Create your account</h1>
        <p class="text-sm text-muted-foreground">30-day free trial — no credit card required</p>
    </div>

    <SocialButtons mode="signup" />

    <form on:submit|preventDefault={submit} class="space-y-4">
        {#if serverError}
            <div class="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {serverError}
            </div>
        {/if}

        <!-- Name row -->
        <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
                <Label for="reg-first">First name</Label>
                <Input id="reg-first" type="text" autocomplete="given-name" placeholder="Jane" bind:value={firstName} disabled={loading} required />
                {#if fieldErrors.first_name}<p class="text-xs text-destructive">{fieldErrors.first_name}</p>{/if}
            </div>
            <div class="space-y-1.5">
                <Label for="reg-last">Last name</Label>
                <Input id="reg-last" type="text" autocomplete="family-name" placeholder="Doe" bind:value={lastName} disabled={loading} required />
                {#if fieldErrors.last_name}<p class="text-xs text-destructive">{fieldErrors.last_name}</p>{/if}
            </div>
        </div>

        <div class="space-y-1.5">
            <Label for="reg-email">Email</Label>
            <Input id="reg-email" type="email" autocomplete="email" placeholder="you@example.com" bind:value={email} disabled={loading} required />
            {#if fieldErrors.email}<p class="text-xs text-destructive">{fieldErrors.email}</p>{/if}
        </div>

        <div class="space-y-1.5">
            <Label for="reg-username">Username</Label>
            <Input id="reg-username" type="text" autocomplete="username" placeholder="janedoe" bind:value={username} disabled={loading} required />
            {#if fieldErrors.username}<p class="text-xs text-destructive">{fieldErrors.username}</p>{/if}
        </div>

        <div class="space-y-1.5">
            <Label for="reg-password">Password</Label>
            <div class="relative">
                <Input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    autocomplete="new-password"
                    placeholder="Min. 6 characters"
                    bind:value={password}
                    disabled={loading}
                    required
                    minlength={6}
                    class="pr-10"
                />
                <button
                    type="button"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    on:click={() => (showPassword = !showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                    {#if showPassword}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21"/></svg>
                    {:else}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    {/if}
                </button>
            </div>
            {#if password}
                <div class="flex items-center gap-2 mt-1">
                    <div class="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                        <div class="h-full rounded-full transition-all {strength.color}" style="width: {(strength.level / 3) * 100}%"></div>
                    </div>
                    <span class="text-xs text-muted-foreground shrink-0">{strength.label}</span>
                </div>
            {/if}
            {#if fieldErrors.password}<p class="text-xs text-destructive">{fieldErrors.password}</p>{/if}
        </div>

        <!-- Age + Terms checkboxes -->
        <div class="space-y-2">
            <div class="flex items-start gap-2">
                <Checkbox id="reg-adult" bind:checked={isAdult} class="mt-0.5" />
                <Label for="reg-adult" class="font-normal leading-snug cursor-pointer">
                    I am 16 years of age or older
                </Label>
            </div>
            {#if fieldErrors.adult}<p class="text-xs text-destructive pl-6">{fieldErrors.adult}</p>{/if}

            <div class="flex items-start gap-2">
                <Checkbox id="reg-terms" bind:checked={agreedToTerms} class="mt-0.5" />
                <Label for="reg-terms" class="font-normal leading-snug cursor-pointer">
                    I agree to the
                    <button type="button" class="text-xerteal-600 hover:underline" on:click|stopPropagation={() => (tosOpen = true)}>Terms of Service</button>
                    and
                    <button type="button" class="text-xerteal-600 hover:underline" on:click|stopPropagation={() => (ppOpen = true)}>Privacy Policy</button>
                </Label>
            </div>
            {#if fieldErrors.terms}<p class="text-xs text-destructive pl-6">{fieldErrors.terms}</p>{/if}
        </div>

        <!--
            reCAPTCHA v2 invisible — this container stays empty; the widget's only
            visible part is the floating "protected by reCAPTCHA" badge Google
            anchors to the corner of the viewport. Keep the badge: hiding it
            obliges us to state the Privacy Policy / Terms notice in the form
            instead, which Google's branding terms require either way.
        -->
        {#if captchaEnabled}
            <div bind:this={recaptchaContainer}></div>
            {#if fieldErrors.captcha}<p class="text-xs text-destructive text-center">{fieldErrors.captcha}</p>{/if}
        {/if}

        <Button type="submit" class="w-full" disabled={loading}>
            {#if loading}
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {/if}
            Create account
        </Button>
    </form>

    <p class="text-center text-sm text-muted-foreground">
        Already have an account?
        <button type="button" class="font-medium text-xerteal-600 hover:underline" on:click={onLogin}>
            Sign in
        </button>
    </p>
</div>

<!-- TOS Dialog -->
<Dialog.Root bind:open={tosOpen}>
    <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content class="max-w-2xl max-h-[80vh] flex flex-col">
            <Dialog.Header>
                <Dialog.Title>Terms of Service</Dialog.Title>
            </Dialog.Header>
            <iframe src="/legal/terms" title="Terms of Service" class="flex-1 min-h-[60vh] border-0 rounded-b-lg"></iframe>
        </Dialog.Content>
    </Dialog.Portal>
</Dialog.Root>

<!-- PP Dialog -->
<Dialog.Root bind:open={ppOpen}>
    <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content class="max-w-2xl max-h-[80vh] flex flex-col">
            <Dialog.Header>
                <Dialog.Title>Privacy Policy</Dialog.Title>
            </Dialog.Header>
            <iframe src="/legal/privacy" title="Privacy Policy" class="flex-1 min-h-[60vh] border-0 rounded-b-lg"></iframe>
        </Dialog.Content>
    </Dialog.Portal>
</Dialog.Root>
